// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable max-lines */
import { isDefined } from "@clipboard-health/util-ts";
import { API_TYPES, get } from "@src/appV2/api";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useExpirableLocalStorage } from "@src/appV2/lib/utils/useExpirableLocalStorage";
import { getUserType } from "@src/appV2/PlacementCandidates/utils/helper";
import {
  type InfiniteData,
  type QueryClient,
  type QueryFunctionContext,
  type QueryKey,
  useInfiniteQuery,
  type UseInfiniteQueryOptions,
} from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { z } from "zod";

import {
  GetPlacementsForWorkplaceQuerySchema,
  type GetPlacementsResponse,
  type HiringEventInclude,
  paginatedGetPlacementsResponseSchema,
  type placementDataSchema,
  type PlacementIncludedApplication,
  type PlacementIncludedInterview,
  type PlacementIncludedWorkplace,
  type PlacementInterviewSettingsInclude,
  type PlacementRequirementInclude,
  type PlacementResponse,
  type PlacementWorkerRequirementInclude,
  type PlacementWorkplaceUserIncluded,
} from "../types/fetchPlacements.schema";
import { computeScheduleType, computeWorkdaysRequired } from "../utils/compute-schedule-type";
import { extractPlacementTags } from "../utils/placementTags";

export function fetchPlacementsApiUrl(workplaceId: string) {
  return `${environmentConfig.REACT_APP_BASE_API_URL}/workplaces/${workplaceId}/placements`;
}

export const getPlacementsForWorkplaceQuerySchema = z.object({
  sort: z.string().optional(),
  filter: z
    .object({
      workplaceId: z.string().optional(),
      status: z.string().optional(),
      workerTypes: z.string().optional(),
      jobTypes: z.string().optional(),
      shiftTypes: z.string().optional(),
      minPay: z
        .object({
          amountInMinorUnits: z.string().optional(),
          currencyCode: z.literal("USD"),
        })
        .optional(),
      maxPay: z
        .object({
          amountInMinorUnits: z.string().optional(),
          currencyCode: z.literal("USD"),
        })
        .optional(),
    })
    .optional(),
});
export type GetPlacementsForWorkplaceQuery = z.infer<typeof getPlacementsForWorkplaceQuerySchema>;

interface FetchPlacementsParams {
  workplaceId: string;
  queryParams: GetPlacementsForWorkplaceQuery;
}

export function getFetchPaginatedPlacementsQueryKey({
  workplaceId,
  queryParams,
}: FetchPlacementsParams) {
  return [fetchPlacementsApiUrl(workplaceId), queryParams];
}

export function useFetchPaginatedPlacements(
  params: FetchPlacementsParams,
  options: UseInfiniteQueryOptions<GetPlacementsResponse, AxiosError> = {}
) {
  const { workplaceId, queryParams } = params;
  const { getValue: getLinkSource } = useExpirableLocalStorage<string | undefined>("page-source");
  const { profile, admin, user } = useSession();
  return useInfiniteQuery({
    queryKey: getFetchPaginatedPlacementsQueryKey({ workplaceId, queryParams }),
    queryFn: async ({
      pageParam: pageParameter = undefined,
    }: QueryFunctionContext<QueryKey, string | undefined>) => {
      const response = await get({
        url: fetchPlacementsApiUrl(workplaceId),
        queryParams: {
          ...queryParams,
          page: {
            size: 20,
            cursor: pageParameter,
          },
        },
        responseSchema: paginatedGetPlacementsResponseSchema,
        requestSchema: GetPlacementsForWorkplaceQuerySchema,
      });
      logEvent(APP_V2_USER_EVENTS.PLACEMENT_PAGE_VIEWED, {
        workplaceId,
        userType: getUserType(profile, admin),
        userId: user?._id,
        email: user?.email,
        queryParams,
        ...(isDefined(getLinkSource("source")) && { linkSource: getLinkSource("source") }),
      });
      return response.data;
    },
    getNextPageParam: (lastPage) => lastPage.links.nextCursor ?? undefined,
    ...options,
  });
}

function transformWorkerRequirements(
  workerRequirementsData: PlacementWorkerRequirementInclude[],
  placementIncludedMetaData: GetPlacementsResponse["included"]
) {
  return workerRequirementsData.map((workerRequirement) => {
    const requirement = placementIncludedMetaData.find(
      (included): included is PlacementRequirementInclude =>
        included.type === API_TYPES.requirement &&
        included.id === workerRequirement.relationships.requirement.data.id
    );
    return {
      ...workerRequirement.attributes,
      id: workerRequirement.id,
      requirement: {
        id: requirement?.id,
        name: requirement?.attributes.name,
      },
    };
  });
}

function transformHiringManagers(
  placement: z.infer<typeof placementDataSchema>,
  placementIncludedMetaData: GetPlacementsResponse["included"]
) {
  const hiringManagersData = placement.relationships.hiringManagers?.data ?? [];
  const hiringManagers = placementIncludedMetaData
    .filter(
      (included): included is PlacementWorkplaceUserIncluded =>
        included.type === API_TYPES.workplaceUser &&
        hiringManagersData.some((hiringManager) => hiringManager.id === included.id)
    )
    .map((hiringManager) => {
      const relationshipData = hiringManagersData.find(
        (hiringManagerData) => hiringManagerData.id === hiringManager.id
      );
      return {
        ...hiringManager.attributes,
        id: hiringManager.id,
        isPrimary: relationshipData?.meta?.isPrimary,
      };
    });

  return [
    ...(hiringManagers ?? []),
    ...(placement.attributes.hiringPOC
      ? [
          {
            email: placement.attributes.hiringPOC.email,
            name: placement.attributes.hiringPOC.name,
            isPrimary: false,
          },
        ]
      : []),
  ];
}

export function updatePaginatedPlacements(
  queryClient: QueryClient,
  queryKey: QueryKey,
  updatedPlacement: PlacementResponse
) {
  // update the selected placement using setQueryData
  queryClient.setQueryData(queryKey, (oldData: InfiniteData<GetPlacementsResponse> | undefined) => {
    if (!oldData) {
      return undefined;
    }

    // it is possible that the included workspace has changed
    const newWorkplaceIncluded = updatedPlacement.included.find(
      (included) => included.type === API_TYPES.workplace
    );

    // get the query params
    const queryParams = queryKey[1] as GetPlacementsForWorkplaceQuery;

    return {
      ...oldData,
      pages: oldData.pages.map((page) => ({
        ...page,
        data: page.data
          .map((placement) =>
            placement.id === updatedPlacement.data.id ? updatedPlacement.data : placement
          )
          // handle the case where the workplace id is changed on the placement
          .filter(
            (placement) =>
              !queryParams.filter?.workplaceId ||
              queryParams.filter?.workplaceId.length === 0 ||
              queryParams.filter?.workplaceId.includes(placement.relationships.workplace.data.id)
          ),
        // update the workplace included if it has changed
        included: page.included.map((included) =>
          included.type === API_TYPES.workplace && included.id === newWorkplaceIncluded?.id
            ? newWorkplaceIncluded
            : included
        ),
      })),
    };
  });

  // mark placement queries with a different filter as stale
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  queryClient.invalidateQueries({
    predicate: (query) => /\/workplaces\/\w+\/placements/.test((query.queryKey[0] ?? "") as string),
  });
}

export function transformResponseIntoPlacement(
  placement: z.infer<typeof placementDataSchema>,
  placementIncludedMetaData: GetPlacementsResponse["included"]
) {
  const workplace = placementIncludedMetaData.find(
    (workplace) =>
      workplace.type === API_TYPES.workplace &&
      workplace.id === placement.relationships.workplace.data.id
  ) as PlacementIncludedWorkplace | undefined;

  if (!workplace) {
    throw new Error("Workplace details not found");
  }

  const interviewSettings = placementIncludedMetaData.find(
    (included): included is PlacementInterviewSettingsInclude =>
      included.type === API_TYPES.interviewSettings &&
      workplace.id === included.relationships.workplace.data.id
  );

  const applications = placement.relationships.applications?.data.map((application) => {
    const applicationData = placementIncludedMetaData.find(
      (included) =>
        included.type === API_TYPES.placementApplication && included.id === application.id
    ) as PlacementIncludedApplication | undefined;
    if (!applicationData) {
      throw new Error("Application details not found");
    }

    return applicationData;
  });

  const hiringEvents = placementIncludedMetaData.filter(
    (included) =>
      included.type === API_TYPES.hiringEvent &&
      placement.relationships.hiringEvent?.data.some(
        (hiringEvent) => hiringEvent.id === included.id
      )
  ) as HiringEventInclude[];

  const workerRequirementsData = placementIncludedMetaData.filter(
    (included): included is PlacementWorkerRequirementInclude =>
      included.type === API_TYPES.workerRequirement &&
      workplace.relationships.workerRequirements.data.some(
        (workerRequirement) => workerRequirement.id === included.id
      ) &&
      included.attributes.qualifications.some((qualification) =>
        placement.attributes.workerTypes?.includes(qualification)
      )
  );
  const workerRequirements = transformWorkerRequirements(
    workerRequirementsData,
    placementIncludedMetaData
  );

  const tags = extractPlacementTags(placement, placementIncludedMetaData);

  const interviews = placementIncludedMetaData.filter(
    (included): included is PlacementIncludedInterview =>
      included.type === API_TYPES.interview &&
      (placement.relationships.interviews?.data.some((interview) => interview.id === included.id) ??
        false)
  );

  return {
    ...placement.attributes,
    id: placement.id,
    tags,
    workplace: {
      id: workplace.id,
      name: (workplace.attributes as { name: string }).name,
    },
    jobShifts: placement.attributes.jobShifts
      .filter((jobShift) => jobShift.enabled)
      .map((jobShift) => {
        const scheduleType = computeScheduleType(jobShift);
        return {
          ...jobShift,
          scheduleType,
          workdaysRequired:
            scheduleType === "flexible"
              ? computeWorkdaysRequired(jobShift.daysOfWeek)
              : jobShift.workdaysRequired,
        };
      }),
    applications,
    interviewSettings,
    interviews,
    hiringEvents,
    workerRequirements,
    hiringManagers: transformHiringManagers(placement, placementIncludedMetaData),
  };
}

export type PlacementWithDetails = ReturnType<typeof transformResponseIntoPlacement>;
